import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    FolderTree,
    Folder,
    FolderPlus,
    FilePlus2,
    Upload,
    StickyNote,
    File as FileIcon,
    ChevronRight,
    Home,
    Trash2,
    Pencil,
    Download,
    X,
    Loader2,
    Search,
    Save,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    listMyDocs,
    createMyDocsFolder,
    createMyDocsNote,
    uploadMyDocsFile,
    myDocsFileUrl,
    getMyDocsItem,
    updateMyDocsItem,
    updateMyDocsFolder,
    deleteMyDocsItem,
    deleteMyDocsFolder,
} from "@/lib/api";

const fmtBytes = (n) => {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const fileIconColor = (ct) => {
    if (!ct) return "text-slate-500";
    if (ct.startsWith("image/")) return "text-pink-500";
    if (ct.includes("pdf")) return "text-red-500";
    if (ct.includes("word")) return "text-blue-500";
    if (ct.includes("excel") || ct.includes("sheet")) return "text-emerald-500";
    if (ct.startsWith("text/")) return "text-amber-500";
    return "text-slate-500";
};

export default function MiDocumentacion() {
    const [parentId, setParentId] = useState(null);
    const [data, setData] = useState({ folders: [], items: [], breadcrumbs: [] });
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [uploading, setUploading] = useState(false);
    const [editingNote, setEditingNote] = useState(null); // {id, title, content} or 'new'
    const fileInput = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await listMyDocs(parentId);
            setData(d);
        } catch {
            toast.error("No se pudo cargar la carpeta");
        } finally {
            setLoading(false);
        }
    }, [parentId]);

    useEffect(() => {
        load();
    }, [load]);

    const handleNewFolder = async () => {
        const name = window.prompt("Nombre de la nueva carpeta:");
        if (!name || !name.trim()) return;
        try {
            await createMyDocsFolder({ name: name.trim(), parent_id: parentId });
            toast.success("Carpeta creada");
            await load();
        } catch {
            toast.error("Error al crear");
        }
    };

    const handleUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            await uploadMyDocsFile(file, parentId);
            toast.success(`"${file.name}" subido`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al subir");
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteFolder = async (f) => {
        if (!window.confirm(`¿Borrar carpeta "${f.name}" y todo su contenido?`)) return;
        try {
            await deleteMyDocsFolder(f.id);
            toast.success("Carpeta eliminada");
            await load();
        } catch {
            toast.error("Error al eliminar");
        }
    };

    const handleDeleteItem = async (it) => {
        if (!window.confirm(`¿Borrar "${it.title}"?`)) return;
        try {
            await deleteMyDocsItem(it.id);
            toast.success("Eliminado");
            await load();
        } catch {
            toast.error("Error al eliminar");
        }
    };

    const handleRenameFolder = async (f) => {
        const n = window.prompt("Nuevo nombre:", f.name);
        if (!n || n.trim() === f.name) return;
        try {
            await updateMyDocsFolder(f.id, { name: n.trim() });
            toast.success("Renombrada");
            await load();
        } catch {
            toast.error("Error al renombrar");
        }
    };

    const handleRenameItem = async (it) => {
        const n = window.prompt("Nuevo nombre:", it.title);
        if (!n || n.trim() === it.title) return;
        try {
            await updateMyDocsItem(it.id, { title: n.trim() });
            toast.success("Renombrado");
            await load();
        } catch {
            toast.error("Error al renombrar");
        }
    };

    const openNote = async (it) => {
        try {
            const full = await getMyDocsItem(it.id);
            setEditingNote(full);
        } catch {
            toast.error("Error al abrir nota");
        }
    };

    const filteredFolders = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return data.folders;
        return data.folders.filter((f) => f.name.toLowerCase().includes(q));
    }, [data.folders, query]);

    const filteredItems = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return data.items;
        return data.items.filter((i) => (i.title || "").toLowerCase().includes(q));
    }, [data.items, query]);

    return (
        <Layout>
            <div className="max-w-[1400px] mx-auto p-6 md:p-8" data-testid="mi-documentacion-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-violet-600 mb-2 flex items-center gap-1.5">
                            <FolderTree size={12} />
                            Espacio personal de archivos y notas
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-black text-ink-950 leading-[1.05]">
                            Mi documentación
                        </h1>
                        <p className="text-base text-ink-500 mt-2 font-medium max-w-2xl">
                            Sube documentos, crea subcarpetas y guarda notas para tener
                            todo lo importante de tu empresa siempre a mano.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setEditingNote({ id: null, title: "", content: "" })}
                            className="rounded-full h-10 px-4 border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700 font-bold gap-1.5"
                            data-testid="mydocs-new-note"
                        >
                            <StickyNote size={14} />
                            Nota
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleNewFolder}
                            className="rounded-full h-10 px-4 border-violet-200 bg-violet-50 hover:bg-violet-100 text-violet-700 font-bold gap-1.5"
                            data-testid="mydocs-new-folder"
                        >
                            <FolderPlus size={14} />
                            Carpeta
                        </Button>
                        <Button
                            onClick={() => fileInput.current?.click()}
                            disabled={uploading}
                            className="rounded-full h-10 px-4 bg-ink-950 hover:bg-ink-900 text-white font-bold gap-1.5 shadow-pop"
                            data-testid="mydocs-upload"
                        >
                            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                            Subir archivo
                        </Button>
                        <input
                            ref={fileInput}
                            type="file"
                            className="hidden"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleUpload(f);
                                e.target.value = "";
                            }}
                        />
                    </div>
                </div>

                {/* Breadcrumbs + search */}
                <div className="flex items-center gap-2 mb-5 flex-wrap">
                    <button
                        onClick={() => setParentId(null)}
                        className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-bold transition-all ${parentId === null ? "bg-ink-950 text-white" : "bg-ink-50 text-ink-600 hover:bg-ink-100"}`}
                        data-testid="mydocs-breadcrumb-root"
                    >
                        <Home size={12} />
                        Raíz
                    </button>
                    {(data.breadcrumbs || []).map((b, idx) => (
                        <React.Fragment key={b.id}>
                            <ChevronRight size={13} className="text-ink-300" />
                            <button
                                onClick={() => setParentId(b.id)}
                                className={`px-3 h-8 rounded-full text-xs font-bold transition-all ${idx === data.breadcrumbs.length - 1 ? "bg-ink-950 text-white" : "bg-ink-50 text-ink-600 hover:bg-ink-100"}`}
                            >
                                {b.name}
                            </button>
                        </React.Fragment>
                    ))}
                    <div className="ml-auto relative">
                        <Search
                            size={13}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
                        />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Buscar en esta carpeta…"
                            className="pl-9 rounded-full h-9 w-64 border-ink-100"
                            data-testid="mydocs-search"
                        />
                    </div>
                </div>

                {/* Grid */}
                {loading ? (
                    <div className="bg-white border border-ink-100 rounded-2xl p-16 text-center">
                        <Loader2 className="mx-auto text-brand-600 animate-spin mb-3" size={28} />
                        <div className="text-sm font-bold text-ink-500">Cargando…</div>
                    </div>
                ) : filteredFolders.length === 0 && filteredItems.length === 0 ? (
                    <EmptyState onUpload={() => fileInput.current?.click()} onNewFolder={handleNewFolder} onNewNote={() => setEditingNote({ id: null, title: "", content: "" })} />
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                        {filteredFolders.map((f, idx) => (
                            <motion.div
                                key={f.id}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.02 }}
                                className="group relative bg-white border border-ink-100 rounded-2xl p-4 cursor-pointer hover:border-violet-300 hover:shadow-pop transition-all"
                                onDoubleClick={() => setParentId(f.id)}
                                data-testid={`mydocs-folder-${f.id}`}
                            >
                                <button
                                    onClick={() => setParentId(f.id)}
                                    className="block w-full text-left"
                                >
                                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white mb-2 shadow-pop">
                                        <Folder size={22} strokeWidth={2.5} fill="white" fillOpacity={0.18} />
                                    </div>
                                    <div className="font-bold text-ink-950 text-sm truncate" title={f.name}>
                                        {f.name}
                                    </div>
                                    <div className="text-[10px] font-mono text-ink-400 mt-0.5">
                                        Carpeta
                                    </div>
                                </button>
                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRenameFolder(f);
                                        }}
                                        className="p-1.5 rounded-md bg-white/90 backdrop-blur hover:bg-blue-50 text-blue-600 shadow-sm"
                                        title="Renombrar"
                                    >
                                        <Pencil size={12} />
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteFolder(f);
                                        }}
                                        className="p-1.5 rounded-md bg-white/90 backdrop-blur hover:bg-red-50 text-red-500 shadow-sm"
                                        title="Eliminar"
                                        data-testid={`mydocs-folder-del-${f.id}`}
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            </motion.div>
                        ))}

                        {filteredItems.map((it, idx) => {
                            const isNote = it.type === "note";
                            return (
                                <motion.div
                                    key={it.id}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: (filteredFolders.length + idx) * 0.02 }}
                                    className="group relative bg-white border border-ink-100 rounded-2xl p-4 cursor-pointer hover:border-brand-300 hover:shadow-pop transition-all"
                                    data-testid={`mydocs-item-${it.id}`}
                                    onClick={() => {
                                        if (isNote) openNote(it);
                                    }}
                                >
                                    {isNote ? (
                                        <>
                                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white mb-2 shadow-pop">
                                                <StickyNote size={20} strokeWidth={2.5} />
                                            </div>
                                            <div className="font-bold text-ink-950 text-sm truncate" title={it.title}>
                                                {it.title}
                                            </div>
                                            <div className="text-[10px] font-mono text-ink-400 mt-0.5">
                                                Nota
                                            </div>
                                        </>
                                    ) : (
                                        <a
                                            href={myDocsFileUrl(it.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="block"
                                        >
                                            <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 mb-2 ${fileIconColor(it.content_type)}`}>
                                                <FileIcon size={22} strokeWidth={2} />
                                            </div>
                                            <div className="font-bold text-ink-950 text-sm truncate" title={it.title}>
                                                {it.title}
                                            </div>
                                            <div className="text-[10px] font-mono text-ink-400 mt-0.5">
                                                {fmtBytes(it.size_bytes)}
                                            </div>
                                        </a>
                                    )}
                                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                                        {!isNote && (
                                            <a
                                                href={myDocsFileUrl(it.id)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="p-1.5 rounded-md bg-white/90 backdrop-blur hover:bg-emerald-50 text-emerald-600 shadow-sm"
                                                title="Descargar"
                                                download
                                            >
                                                <Download size={12} />
                                            </a>
                                        )}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRenameItem(it);
                                            }}
                                            className="p-1.5 rounded-md bg-white/90 backdrop-blur hover:bg-blue-50 text-blue-600 shadow-sm"
                                            title="Renombrar"
                                        >
                                            <Pencil size={12} />
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteItem(it);
                                            }}
                                            className="p-1.5 rounded-md bg-white/90 backdrop-blur hover:bg-red-50 text-red-500 shadow-sm"
                                            title="Eliminar"
                                            data-testid={`mydocs-item-del-${it.id}`}
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}

                {/* Note editor modal */}
                <AnimatePresence>
                    {editingNote !== null && (
                        <NoteEditor
                            note={editingNote}
                            parentId={parentId}
                            onClose={() => setEditingNote(null)}
                            onSaved={async () => {
                                setEditingNote(null);
                                await load();
                            }}
                        />
                    )}
                </AnimatePresence>
            </div>
        </Layout>
    );
}

function EmptyState({ onUpload, onNewFolder, onNewNote }) {
    return (
        <div className="bg-white border border-ink-100 border-dashed rounded-2xl p-16 text-center">
            <FolderTree className="mx-auto text-ink-300 mb-3" size={42} />
            <div className="font-display text-2xl font-black text-ink-950 mb-1">
                Carpeta vacía
            </div>
            <div className="text-sm text-ink-500 font-medium mb-6">
                Empieza subiendo un archivo, creando una nota o una subcarpeta.
            </div>
            <div className="flex gap-2 justify-center flex-wrap">
                <Button onClick={onUpload} className="rounded-full h-10 px-5 bg-ink-950 text-white font-bold gap-1.5">
                    <Upload size={14} /> Subir archivo
                </Button>
                <Button onClick={onNewFolder} variant="outline" className="rounded-full h-10 px-5 font-bold gap-1.5 border-violet-200 text-violet-700 bg-violet-50 hover:bg-violet-100">
                    <FolderPlus size={14} /> Crear carpeta
                </Button>
                <Button onClick={onNewNote} variant="outline" className="rounded-full h-10 px-5 font-bold gap-1.5 border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100">
                    <StickyNote size={14} /> Nueva nota
                </Button>
            </div>
        </div>
    );
}

function NoteEditor({ note, parentId, onClose, onSaved }) {
    const isNew = !note?.id;
    const [title, setTitle] = useState(note?.title || "");
    const [content, setContent] = useState(note?.content || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!title.trim()) {
            toast.error("Pon un título");
            return;
        }
        setSaving(true);
        try {
            if (isNew) {
                await createMyDocsNote({ title: title.trim(), content, parent_id: parentId });
                toast.success("Nota creada");
            } else {
                await updateMyDocsItem(note.id, { title: title.trim(), content });
                toast.success("Nota guardada");
            }
            onSaved();
        } catch {
            toast.error("Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
                data-testid="mydocs-note-editor"
            >
                <div className="flex items-center justify-between p-4 border-b border-ink-100">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-600 flex items-center gap-1.5">
                        <StickyNote size={12} />
                        {isNew ? "Nueva nota" : "Editar nota"}
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-ink-50">
                        <X size={18} />
                    </button>
                </div>
                <div className="p-5 space-y-3 flex-1 overflow-auto">
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                            Título
                        </Label>
                        <Input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Ej. Nota de la mesa del 12/06"
                            className="font-display text-lg font-black"
                            data-testid="mydocs-note-title"
                        />
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                            Contenido
                        </Label>
                        <Textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder="Escribe aquí…"
                            rows={16}
                            className="resize-none font-mono text-sm"
                            data-testid="mydocs-note-content"
                        />
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 p-4 border-t border-ink-100">
                    <Button onClick={onClose} variant="outline" className="rounded-full h-10 px-5 font-bold">
                        Cancelar
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="rounded-full h-10 px-5 bg-ink-950 hover:bg-ink-900 text-white font-bold gap-1.5"
                        data-testid="mydocs-note-save"
                    >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        Guardar
                    </Button>
                </div>
            </motion.div>
        </motion.div>
    );
}
